import { useMemo, useState } from 'react'
import {
  Alert, Anchor, Button, Divider, Group, Loader, Select, Stack,
  Text, TextInput, Title,
} from '@mantine/core'
import { IconInfoCircle, IconLock, IconShieldCheck } from '@tabler/icons-react'
import { DataCard, EmptyState } from '@/components/ui'
import { useCan } from '@/store/auth'
import { useManagedOrganizations, useOrganizations } from '@/hooks/useOrganizations'
import {
  useCriarSocTeam,
  useSocTeams,
  useSocTenants,
} from '@/hooks/useSoc'
import { AlertasEmAndamento } from '@/components/admin/soc/AlertasEmAndamento'
import { ConfiguracaoDeAlertas } from '@/components/admin/soc/ConfiguracaoDeAlertas'
import { EditorDeIntegrantes } from '@/components/admin/soc/EditorDeIntegrantes'
import { TenantDoTime } from '@/components/admin/soc/TenantDoTime'
import { nomeDerivado } from './socLabels'

/**
 * Área de SOC — time por tenant e o que cada alerta dispara.
 *
 * O QUE ESTA TELA NÃO PODE DEIXAR DE DIZER, porque o sistema não consegue
 * garantir sozinho:
 *
 *  1. Nível é quem PERTENCE, não quem está de turno. Escala de plantão está
 *     fora de escopo, e a tela não pode dar a entender o contrário.
 *  2. O alarme é melhor esforço. Web Push não fura silencioso nem Não Perturbe.
 *  3. Fonte que nunca entregou aparece marcada — configurar sobre ela é
 *     prometer o que o sistema não faz.
 *
 * CRITICO (revisao final da Fase 5b): `teamId` so nascia do `onSuccess` de
 * `criar.mutate` — apos um F5, ou para o time que ja existia ANTES desta
 * tela, nao havia como chegar ao editor de integrantes: o Select de tenant
 * desabilita justamente quem `ja_tem_soc`, e nao existia outro caminho para
 * dentro. E o defeito com nome e sobrenome no docstring de
 * `GET /admin/soc/teams` (`alert_admin_routes.py`) — o time de producao sem
 * NINGUEM nos tres niveis so existe porque nao havia como abrir o editor dele
 * de novo. A correcao: `useSocTeams` alimenta um segundo Select ("Time
 * existente") que POPULA `teamId` direto, sem passar pelo fluxo de criacao.
 *
 * Cor, tipografia e espaçamento saem de docs/DESIGN_SYSTEM.md (DS §2.3 para a
 * semântica de cor, DS §3.4 para alinhamento à esquerda). Nada é escolhido aqui.
 */

export function SocTeamsAdmin() {
  const can = useCan()
  // Gate OU (Fase 5a, 2026-08-26): `soc:manage` de plataforma (derivada de
  // `alerts:manage` pela MIGRATION_243, conferida em produção para
  // Administrador e DEVOPS) OU gestor de ≥1 organização — mesmo padrão de
  // Organizacoes.tsx. O backend continua a autoridade: `pode_administrar_soc`
  // devolve 403 nas rotas para quem não se enquadra em nenhum dos dois.
  const canManageSoc = can('soc', 'manage')
  const { data: managedOrgs = [], isLoading: loadingManaged } = useManagedOrganizations()
  // Quem entra por `soc:manage` usa a lista GERAL de organizações — não
  // `managedOrgs`, que é "eu sou gestor/responsável de", vazia para quem
  // administra por ser plataforma. `GET /api/v1/organizations` já é escopada
  // no servidor por `organization_ids_visible_to_user` (api.py:23297): admin
  // vê todas, os demais só as visíveis a ele.
  //
  // LIMITE DECLARADO: `soc:manage` não implica `admin:platform` — é o caso do
  // template DEVOPS (5 usuários). Para eles esta lista pode ser MENOS que
  // "todas as organizações", apesar do rótulo do template sugerir o
  // contrário. Não resolvido aqui de propósito: registrado para o próximo não
  // descobrir por acidente.
  const { data: allOrgs = [], isLoading: loadingOrgs } = useOrganizations({ enabled: canManageSoc })

  if (!canManageSoc && loadingManaged) {
    return <Loader size="sm" />
  }
  if (!canManageSoc && managedOrgs.length === 0) {
    return (
      <Alert color="gray" variant="light" icon={<IconLock size={16} />}>
        Você não gerencia nenhum time de SOC. A Área de SOC fica disponível para
        quem administra o time de alertas da plataforma ou é gestor/responsável
        de uma Organização. Peça a um administrador para atribuí-lo como gestor
        ou responsável de uma Organização.
      </Alert>
    )
  }
  if (canManageSoc && loadingOrgs) {
    return <Loader size="sm" />
  }

  const orgs = (canManageSoc ? allOrgs : managedOrgs).map((o) => ({ id: o.id, name: o.name }))
  return <SocTeamsAdminConteudo orgs={orgs} />
}

function SocTeamsAdminConteudo({ orgs }: { orgs: { id: number; name: string }[] }) {
  // Organização só existe uma: já entra escolhida, sem obrigar clique — é o
  // caso da maioria dos gestores. `useState` de inicializador (não `useEffect`)
  // porque só nasce assim uma vez; trocar de tela e voltar reavalia do zero.
  const [orgId, setOrgId] = useState<number | null>(() => (orgs.length === 1 ? orgs[0].id : null))
  const [tenantId, setTenantId] = useState<number | null>(null)
  const [nome, setNome] = useState('')
  const [teamId, setTeamId] = useState<number | null>(null)

  const tenants = useSocTenants(orgId)
  const criar = useCriarSocTeam()
  const times = useSocTeams()

  const tenantEscolhido = useMemo(
    () => (tenants.data ?? []).find((t) => t.id === tenantId) ?? null,
    [tenants.data, tenantId],
  )

  // Times ja existentes da organizacao escolhida — e o caminho que faltava
  // para o time SEM ninguem nos tres niveis (o motivo da fase existir): sem
  // isto, `teamId` so nascia da criacao, e um time criado antes desta tela
  // (ou perdido num F5) ficava inalcancavel para sempre.
  const timesDaOrg = useMemo(
    // Pela organizacao ATUAL do tenant, nao pela de origem do time: e a mesma
    // derivacao do gate por time (TASK-11546).
    () => (times.data?.items ?? []).filter(
      (t) => orgId != null && t.organizacoes_do_tenant.includes(orgId),
    ),
    [times.data, orgId],
  )
  const timeSelecionado = (times.data?.items ?? []).find((t) => t.id === teamId) ?? null
  // Tenant fora de toda organizacao: o time continua alarmando, mas nao cabe em
  // seletor nenhum acima (que parte da organizacao). O backend so devolve esses
  // times a plataforma, e sem esta lista nem ela chegava ao editor (spec §4.1).
  const semOrganizacao = (times.data?.items ?? []).filter(
    (t) => t.organizacoes_do_tenant.length === 0,
  )

  function escolherTenant(id: number | null) {
    setTenantId(id)
    const t = (tenants.data ?? []).find((x) => x.id === id)
    // O nome é PRÉ-PREENCHIDO e editável: derivar sem deixar mudar seria impor,
    // e pedir sem sugerir seria trabalho manual num valor que o banco já tem.
    setNome(t ? nomeDerivado(t) : '')
  }

  return (
    <Stack gap="lg">
      {/* Task 4 (2026-08-31): esta tela avulsa fica (rota /admin/soc não sai,
          reversão fácil), mas saiu do menu — a aba "Times de SOC" da tela de
          Organização fecha o ciclo inteiro (listar, abrir, criar, povoar).
          Quem tinha a URL salva precisa saber que mudou de lugar, e para
          onde — não só que mudou. */}
      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        A área de SOC agora fica na tela de Organização, na aba "Times de SOC".{' '}
        <Anchor href="/admin/organizacoes" size="sm">
          Ir para Organização
        </Anchor>
        .
      </Alert>

      <div>
        <Title order={2}>Área de SOC</Title>
        <Text c="dimmed" size="sm">
          Um time por tenant, com três níveis. O que cada alerta dispara, e em qual
          nível ele entra.
        </Text>
      </div>

      {semOrganizacao.length > 0 && (
        <Alert color="yellow" variant="light" title="Times sem organização">
          <Stack gap="xs">
            <Text size="sm">
              O tenant destes times não pertence a nenhuma organização. Só a
              plataforma os administra por aqui, e eles continuam alarmando.
            </Text>
            <Group gap="xs">
              {semOrganizacao.map((t) => (
                <Button
                  key={t.id}
                  size="xs"
                  variant="light"
                  color="yellow"
                  aria-label={`Abrir ${t.name}`}
                  onClick={() => {
                    setOrgId(null)
                    escolherTenant(null)
                    setTeamId(t.id)
                  }}
                >
                  {t.name} — {t.tenant_name}
                </Button>
              ))}
            </Group>
          </Stack>
        </Alert>
      )}

      <DataCard title="Time de SOC" accent="primary">
        <Stack gap="md">
          <Select
            label="Organização"
            placeholder="Selecione a organização"
            data={orgs.map((o) => ({ value: String(o.id), label: o.name }))}
            value={orgId ? String(orgId) : null}
            onChange={(v) => {
              setOrgId(v ? Number(v) : null)
              escolherTenant(null)
              // Time de outra organizacao nao pode continuar aberto — trocar
              // de organizacao e trocar de contexto inteiro.
              setTeamId(null)
            }}
          />

          {/* CRITICO: sem este Select, `teamId` so nascia da criacao — o
              time que ja existe (em producao, ou de uma sessao anterior)
              ficava sem caminho ate o editor. Escolher aqui POPULA `teamId`
              direto, sem passar pelo fluxo de "Criar time" abaixo.

              `times.isError` tratado por `error` (nao por deixar cair no
              MESMO `[]` de "nenhum time criado ainda"): sem isso, uma leitura
              que falha (rede/403/500) reabria o mesmissimo beco sem saida que
              este Select existe para fechar — o operador via o tenant
              desabilitado no Select de baixo (`ja_tem_soc`, de outro
              endpoint, que continua funcionando) e nenhum time selecionavel
              aqui, sem uma palavra dizendo que a LEITURA falhou. */}
          <Select
            label="Time existente"
            placeholder={
              !orgId
                ? 'Escolha a organização primeiro'
                : times.isError
                  ? 'Não foi possível carregar'
                  : timesDaOrg.length === 0
                    ? 'Nenhum time criado ainda nesta organização'
                    : 'Selecione um time já criado'
            }
            error={times.isError ? 'Não foi possível carregar os times existentes.' : undefined}
            disabled={!orgId || timesDaOrg.length === 0}
            value={teamId ? String(teamId) : null}
            onChange={(v) => setTeamId(v ? Number(v) : null)}
            data={timesDaOrg.map((t) => ({
              value: String(t.id),
              // O nome e texto livre: o tenant no rotulo e o que diz onde o
              // time atua de verdade.
              label: `${t.name} — ${t.tenant_name} · ${t.tenant_environment.toUpperCase()}${t.is_active ? '' : ' — desligado'}`,
            }))}
          />

          <Divider label="ou crie um novo time" labelPosition="left" />

          <Select
            label="Tenant monitorado"
            description="O tenant não muda depois de criado. Para atuar em outro tenant, crie outro time."
            placeholder={orgId ? 'Selecione o tenant' : 'Escolha a organização primeiro'}
            disabled={!orgId}
            value={tenantId ? String(tenantId) : null}
            onChange={(v) => escolherTenant(v ? Number(v) : null)}
            data={(tenants.data ?? []).map((t) => ({
              value: String(t.id),
              // Tenant que já tem time aparece DESABILITADO, com o motivo: o
              // índice permite um time de SOC por tenant, e deixar tentar para
              // receber 409 é fazer o operador descobrir errando.
              label: t.ja_tem_soc
                ? `${t.name}-${t.environment.toUpperCase()} — já possui time`
                : `${t.name}-${t.environment.toUpperCase()}`,
              disabled: t.ja_tem_soc,
            }))}
          />

          <TextInput
            label="Nome do time"
            description="Sugerido a partir do tenant, e editável."
            value={nome}
            onChange={(e) => setNome(e.currentTarget.value)}
            disabled={!tenantEscolhido}
          />

          <Group justify="flex-start">
            <Button
              disabled={!orgId || !tenantId}
              loading={criar.isPending}
              onClick={() =>
                criar.mutate(
                  { organization_id: orgId!, tenant_id: tenantId!, name: nome || undefined },
                  { onSuccess: (d) => setTeamId(d.id) },
                )
              }
            >
              Criar time
            </Button>
          </Group>
        </Stack>
      </DataCard>

      {teamId ? (
        <>
          {timeSelecionado && <TenantDoTime time={timeSelecionado} />}
          <DataCard title="Integrantes do time" accent="primary">
            <EditorDeIntegrantes teamId={teamId} />
          </DataCard>
          {/* ANTES da configuracao, de proposito: quem abre esta tela com um
              alarme tocando quer ver o alarme, nao a configuracao dele. */}
          <AlertasEmAndamento teamId={teamId} />
          <ConfiguracaoDeAlertas teamId={teamId} />
        </>
      ) : (
        <EmptyState
          icon={IconShieldCheck}
          title="Nenhum time de SOC selecionado"
          description="Crie o time do tenant para configurar quais alertas disparam o alarme, em qual nível cada um entra e depois de quantos minutos ele sobe."
        />
      )}
    </Stack>
  )
}


export default SocTeamsAdmin
